import { useContext } from 'react';
import { Button, ButtonGroup, ButtonToolbar } from 'reactstrap';
import { sectionTypes } from '../../constants/fields';
import { mapModes } from '../../constants/map';
import { MapContext } from '../../contexts/MapContext';
import { reverseLineString } from '../../utils/geojson';
import RowIcon from '../icons/RowIcon';
import SectionsIcon from '../icons/SectionsIcon';
import UndoRedoButtons from './buttons/UndoRedoButtons';
import DeletingToolbar from './DeletingToolbar';
import { sectionHasGeodata } from '../../utils/sections';
import { captureEvent } from '../../utils/analytics';
import CreateHeadlandButton from './buttons/CreateHeadlandButton';
import ButtonWithShortcut from '../basics/ButtonWithShortcut';
import { SHORTCUTS } from '../../constants/shortcuts';

const modesForType = {
  [sectionTypes.GRID]: ['DRAW', 'EDIT', 'ROTATE', 'OFFSET'],
  [sectionTypes.LINE]: ['DRAW', 'EDIT'],
  [sectionTypes.IMPORTED]: ['EDIT'],
  [sectionTypes.KEYLINE]: ['DRAW', 'EDIT'],
};

const shortCutsForMode = {
  DRAW: SHORTCUTS.DRAW.key,
  EDIT: SHORTCUTS.EDIT.key,
  ROTATE: SHORTCUTS.ROTATE.key,
  OFFSET: SHORTCUTS.OFFSET.key,
};

const SectionToolbar = () => {
  const {
    fields: { updateFieldSection, duplicateFieldSection, removeFieldSection },
    mapState: {
      mapMode,
      setMapMode,
      activeField,
      drawRef,
      context: { keylineMode = false },
    },
  } = useContext(MapContext);

  const { fieldFeature, rowFeature, type, id } = activeField || {};

  const modes = modesForType[type];

  if (mapMode === mapModes.DELETING) {
    return (
      <DeletingToolbar
        name={activeField.name}
        onCancel={() => {
          setMapMode(mapModes.SECTION_ENGAGED);
        }}
        onConfirm={() => {
          removeFieldSection(activeField.id);
          setMapMode(mapModes.VIEW);
        }}
      />
    );
  }

  const onFlipClick = () => {
    const reversed = reverseLineString(fieldFeature);
    updateFieldSection(id, {
      fieldFeature: reversed,
    });
    if (mapMode === mapModes.EDIT && drawRef.current) {
      drawRef.current.delete(id).add({ ...reversed, id });
      drawRef.current.changeMode('direct_select', {
        featureId: id,
      });
    }
  };

  return (
    <ButtonToolbar>
      {type === sectionTypes.KEYLINE && (
        <ButtonGroup className="mr-2">
          <Button
            onClick={() =>
              keylineMode &&
              setMapMode(fieldFeature ? mapModes.EDIT : mapModes.DRAW, id, {
                keylineMode: false,
              })
            }
            outline={keylineMode}
            color={keylineMode ? 'secondary' : 'primary'}
            size="sm"
          >
            <SectionsIcon />
          </Button>
          <Button
            onClick={() =>
              !keylineMode &&
              setMapMode(rowFeature ? mapModes.EDIT : mapModes.DRAW, id, {
                keylineMode: true,
              })
            }
            outline={!keylineMode}
            color={keylineMode ? 'primary' : 'secondary'}
            size="sm"
            disabled={!fieldFeature}
          >
            <RowIcon />
          </Button>
        </ButtonGroup>
      )}
      {modes.length > 0 && (
        <ButtonGroup className="mr-2">
          {modes.map((mode) => {
            const isActive = mapMode === mapModes[mode];
            const feature = keylineMode ? rowFeature : fieldFeature;
            const disabled = mode !== 'DRAW' && !feature;
            return (
              <ButtonWithShortcut
                shortcut={shortCutsForMode[mode]}
                key={mode}
                onClick={() =>
                  setMapMode(
                    mapModes[mode],
                    id,
                    type === sectionTypes.KEYLINE ? { keylineMode } : undefined,
                  )
                }
                className="text-capitalize"
                color={isActive ? 'primary' : 'secondary'}
                size="sm"
                outline={!isActive}
                disabled={disabled}
              >
                {mode.toLowerCase()}
              </ButtonWithShortcut>
            );
          })}
        </ButtonGroup>
      )}
      {type === sectionTypes.LINE && (
        <Button
          onClick={onFlipClick}
          className="mr-2"
          outline
          size="sm"
          disabled={!fieldFeature}
        >
          Flip
        </Button>
      )}
      <Button
        onClick={() => {
          captureEvent('section-duplicated');
          const { id: newId } = duplicateFieldSection(id);
          setMapMode(mapModes.SECTION_ENGAGED, newId);
        }}
        disabled={!sectionHasGeodata(activeField)}
        size="sm"
        outline
        className="mr-2"
      >
        Duplicate
      </Button>
      <Button
        onClick={() => setMapMode(mapModes.DELETING)}
        size="sm"
        outline
        className="mr-2"
      >
        Delete
      </Button>
      {fieldFeature?.geometry?.type === 'Polygon' && (
        <CreateHeadlandButton
          name={`${activeField.name} Headland`}
          feature={activeField.fieldFeature}
          isInnerFeature
          onCreate={() =>
            captureEvent('headland-created-from-feature', {
              featureType: 'section',
            })
          }
          className="mr-2"
          outline
          size="sm"
        />
      )}
      <ButtonWithShortcut
        shortcut={SHORTCUTS.CLOSE.key}
        onClick={() => setMapMode(mapModes.VIEW)}
        color="primary"
        size="sm"
      >
        {/* DRAW mode doesn't make any changes until it is completed */}
        {mapMode === mapModes.DRAW ? 'Cancel' : 'Done'}
      </ButtonWithShortcut>
      <UndoRedoButtons sectionId={id} className="ml-3" />
    </ButtonToolbar>
  );
};

export default SectionToolbar;
